/*
 * Tiny bridge between the SEO layer (lib/Seo.js) and the page-view tracker
 * (components/PageViewTracker.js). Deliberately dependency-free so it adds nothing to the
 * shared bundle.
 *
 * Every <Seo> runs an effect keyed on its title AFTER React has committed the hoisted <title> to
 * the DOM (React 19 renders react-helmet-async's tags as native hoistable elements, so this is
 * the reliable signal — Helmet's own onChangeClientState never fires on React 19). We record
 * which route (path + query) that commit belonged to and broadcast a DOM event.
 * The tracker waits for the commit that belongs to its own route, so `spa_page_view` always
 * carries the route's real title — never the previous page's or the generic shell title.
 *
 * Two race conditions are covered:
 *   - the commit lands BEFORE the tracker's effect subscribes (the page's <Seo> is a child of
 *     the router while the tracker sits beside it, and StrictMode re-runs effects): the
 *     subscribe call checks the last recorded key and fires immediately;
 *   - during a lazy-route transition the outgoing page's <Seo> unmounts and nothing commits
 *     until the new page renders; every commit is stamped with the CURRENT location, so a stale
 *     commit can never satisfy a newer route's wait.
 */
const SEO_COMMIT_EVENT = 'idb:seo-commit';
let lastCommitKey = null;

export const currentRouteKey = () => `${window.location.pathname}${window.location.search}`;

/** Called by <Seo>'s Helmet once its head tags (incl. <title>) are committed to the DOM. */
export function notifySeoCommit() {
  try {
    lastCommitKey = currentRouteKey();
    window.dispatchEvent(new Event(SEO_COMMIT_EVENT));
  } catch (e) {
    // Never let analytics plumbing break rendering.
  }
}

/**
 * Run `cb` once a <Seo> commit for `routeKey` (path + query) has happened. Fires synchronously
 * if that commit already landed. Returns an unsubscribe function.
 */
export function onSeoCommit(routeKey, cb) {
  if (lastCommitKey === routeKey) {
    cb();
    return () => {};
  }
  const handler = () => {
    if (lastCommitKey === routeKey) cb();
  };
  window.addEventListener(SEO_COMMIT_EVENT, handler);
  return () => window.removeEventListener(SEO_COMMIT_EVENT, handler);
}
